import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import HBox from 'components/common/HBox';
import CaptionBox from 'components/common/CaptionBox';
import VBox from 'components/common/VBox';
import AbnormalInsulinBoxChart from '../../../charts/AbnormalInsulinBoxChart';
import AbnormalCircle from 'components/common/AbnormalCircle';
import DoughnutChart from 'components/charts/DoughnutChart';
import Dot from 'components/common/Dot';
import SummaryBox from '../ReportBloodGlocose/SummaryBox';
import React from 'react';
import useRect from 'hooks/common/useRect';
import useSetChartData from 'hooks/app/useSetChartData';

export default function InsulinRatio(props) {
  const {
    insulin_data,
    duration,
    ...rest
  } = props;
  const {
    bld_sugr_cnt,
    after_bolus_abnormal_rate,
    high_bld_sugr_cnt,
    low_bld_sugr_cnt,
    complex_bld_sugr_cnt,
    abnormal_bld_sugr_graph_list,
  } = insulin_data;

  const ref = React.useRef();
  const rect = useRect(ref);
  const setChartDoughnutData = useSetChartData('InsulinDistributionDoughnutChart');
  const setChartScatterData = useSetChartData('InsulinDistributionScatterChart');
  const chartDataTimeoutRef = React.useRef({});
  const onComplete = React.useCallback((type, setChartData) => (animation) => {
    if (insulin_data.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current[type]);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current[type] = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [duration, insulin_data.uninitialized]);

  const doughnutChart = React.useMemo(() => (
    <DoughnutChart
      {...{
        caption: T('볼루스 {0}건', bld_sugr_cnt),
        value: [bld_sugr_cnt - high_bld_sugr_cnt - low_bld_sugr_cnt - complex_bld_sugr_cnt, high_bld_sugr_cnt, low_bld_sugr_cnt, complex_bld_sugr_cnt],
        onComplete: onComplete('setChartDoughnutData', setChartDoughnutData),
        duration,
      }}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [bld_sugr_cnt, high_bld_sugr_cnt, low_bld_sugr_cnt, complex_bld_sugr_cnt, onComplete, setChartDoughnutData, duration, rect]);

  const scatterChart = React.useMemo(() => (
    <AbnormalInsulinBoxChart {...{ abnormal_bld_sugr_graph_list, onComplete: onComplete('setChartScatterData', setChartScatterData), duration }} />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [abnormal_bld_sugr_graph_list, duration, onComplete, setChartScatterData, rect]);

  return (
    <HBox
      {...ex('InsulinRatio', rest)}
      {...{ ref }}
    >
      <VBox>
        <HBox {...ex('SummaryTitle')} between>
          <div>
            {T('인슐린 분포')}
          </div>
        </HBox>
        <HBox  {...ex('AbnormalDistribution')}>
          <VBox>
            <div>
              {T('볼루스후 이상혈당 발생')}
            </div>
            <HBox {...ex('InsulinAbnormalRatio')}>
              <div>
                {after_bolus_abnormal_rate}
              </div>
              <div>
                {T('%')}
              </div>
            </HBox>
            <HBox {...ex('AbnormalSummary')}>
              <SummaryBox
                {...ex('Sub', {
                  caption: T('고혈당'),
                  value: high_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#FF995A' }} />
              </SummaryBox>
              <SummaryBox
                {...ex('Sub', {
                  caption: T('저혈당'),
                  value: low_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#756CE4' }} />
              </SummaryBox>
              <SummaryBox
                {...ex('Sub', {
                  caption: (
                    <VBox>
                      <div>
                        {T('변동혈당')}
                      </div>
                      <div>
                        {T('(고+저혈당)')}
                      </div>
                    </VBox>
                  ),
                  value: complex_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#6ED5E1' }} />
              </SummaryBox>
            </HBox>
          </VBox>
          <div>
            {doughnutChart}
          </div>
        </HBox>
      </VBox>
      {scatterChart}
    </HBox >
  );
}
